import React, {
  createContext,
  useState,
  useMemo,
  useContext,
  useCallback
} from "react"
import { noop } from "lib/helpers"
import { primaryMenuItems } from "lib/menuItems"

// Default to having the first menu item and its sub items open
const defaultMenuState = primaryMenuItems.flatMap(item => [
  item.id,
  ...(item.items?.map(subItem => subItem.id) ?? [])
])

export const MenuContext = createContext<{
  menuState: string[]
  toggleMenuItem: (id: string) => void
}>({
  menuState: defaultMenuState,
  toggleMenuItem: noop
})

export const MenuProvider = ({ children }: { children: React.ReactChild }) => {
  const [menuState, setMenuState] = useState<string[]>(() => {
    // try/catch in case localStorage is blocked
    try {
      const existing = window.localStorage.getItem(`menuState`)
      // If there's an existing menuState, only use it if it's an array
      if (existing) {
        const parsed = JSON.parse(existing)
        if (Array.isArray(parsed)) return parsed as string[]
      }
    } catch {
      return defaultMenuState
    }

    return defaultMenuState
  })

  const toggleMenuItem = useCallback((id: string) => {
    setMenuState(currentState => {
      // Add or remove item based on if it's open already
      const updatedState = currentState.includes(id)
        ? currentState.filter(item => item !== id)
        : [...currentState, id]
      // Write the new state to localStorage
      window.localStorage.setItem(`menuState`, JSON.stringify(updatedState))
      return updatedState
    })
  }, [])

  const value = useMemo(
    () => ({
      menuState,
      toggleMenuItem
    }),
    [menuState, toggleMenuItem]
  )

  return <MenuContext.Provider value={value}>{children}</MenuContext.Provider>
}

// Provide a utility hook for accessing the menu state context
export const useMenuContext = () => {
  const context = useContext(MenuContext)
  if (context === undefined) {
    throw new Error(`useMenuContext must be used within a MenuProvider`)
  }

  return context
}
